/**
 * Safe highlighting utilities to prevent XSS attacks
 * 
 * These utilities provide secure ways to highlight search terms in text
 * without exposing the application to XSS vulnerabilities.
 */

/**
 * Escapes HTML special characters to prevent XSS attacks
 * @param text The text to escape
 * @returns HTML-escaped text
 */
export const escapeHtml = (text: string): string => {
  if (!text) return ''
  
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

/**
 * Split text on a literal search term (case-insensitive) without building a RegExp.
 * Avoids ReDoS from user-supplied search strings.
 */
const splitByLiteralIgnoreCase = (
  text: string,
  term: string
): Array<{ text: string; highlighted: boolean }> => {
  const parts: Array<{ text: string; highlighted: boolean }> = []
  const lowerText = text.toLowerCase()
  const lowerTerm = term.toLowerCase()
  const termLength = term.length
  let lastIndex = 0
  let index = lowerText.indexOf(lowerTerm)

  while (index !== -1) {
    if (index > lastIndex) {
      parts.push({ text: text.slice(lastIndex, index), highlighted: false })
    }
    parts.push({
      text: text.slice(index, index + termLength),
      highlighted: true
    })
    lastIndex = index + termLength
    index = lowerText.indexOf(lowerTerm, lastIndex)
  }

  if (lastIndex < text.length || parts.length === 0) {
    parts.push({ text: text.slice(lastIndex), highlighted: false })
  }

  return parts
}

/**
 * Safely highlights search terms in text using Vue's built-in text interpolation
 * This function returns an object with highlighted and non-highlighted parts
 * that can be safely rendered without v-html
 * 
 * @param text The text to highlight within
 * @param searchTerm The search term to highlight
 * @returns Object with highlighted parts for safe rendering
 */
export const createSafeHighlight = (text: string, searchTerm: string): {
  parts: Array<{ text: string; highlighted: boolean }>
  hasHighlighting: boolean
} => {
  // Parts rendered via Vue {{ }} interpolation are XSS-safe without manual escaping.
  // Do NOT call escapeHtml() here — that would cause literal &gt; etc. to appear as text.
  if (!text || !searchTerm || !searchTerm.trim()) {
    return {
      parts: [{ text: text || '', highlighted: false }],
      hasHighlighting: false
    }
  }

  const parts = splitByLiteralIgnoreCase(text, searchTerm.trim())

  return {
    parts,
    hasHighlighting: parts.some(part => part.highlighted)
  }
}

/**
 * Safely highlights search terms in text and returns HTML string
 * This function should only be used when you need HTML output and can guarantee
 * the input text is already sanitized
 * 
 * @param text The text to highlight within (must be pre-sanitized)
 * @param searchTerm The search term to highlight
 * @returns HTML string with highlighted terms wrapped in <em class="search-highlight">
 */
export const createSafeHighlightHtml = (text: string, searchTerm: string): string => {
  if (!text || !searchTerm || !searchTerm.trim()) {
    return escapeHtml(text || '')
  }

  const cleanText = escapeHtml(text)
  const cleanSearchTerm = escapeHtml(searchTerm.trim())

  return splitByLiteralIgnoreCase(cleanText, cleanSearchTerm)
    .map((part) =>
      part.highlighted
        ? `<em class="search-highlight">${part.text}</em>`
        : part.text
    )
    .join('')
}

/**
 * Safely processes text that may contain HTML entities and highlighting markup
 * This function is designed to handle text that comes from the API and may
 * already contain highlighting markup or HTML entities
 * 
 * @param text The text to process
 * @param searchTerm The search term to highlight
 * @returns Safe HTML string with proper highlighting
 */
export const processApiHighlightedText = (text: string, searchTerm: string): string => {
  if (!text) return ''
  
  // First, decode any HTML entities that might be in the text
  const decodedText = decodeHtmlEntities(text)
  
  // Check if the text already contains highlighting markup (old format from server)
  if (decodedText.includes('<em class="highlight">') || decodedText.includes('<em class=&quot;highlight&quot;>')) {
    // The text already has highlighting, but we need to ensure it's safe and convert to new class
    return sanitizeHighlightedText(decodedText)
  }
  
  // Apply our own safe highlighting
  return createSafeHighlightHtml(decodedText, searchTerm)
}

/**
 * Decodes HTML entities in text
 * @param text The text containing HTML entities
 * @returns Text with HTML entities decoded
 */
export const decodeHtmlEntities = (text: string): string => {
  if (!text) return ''
  
  if (typeof document !== 'undefined') {
    // Use browser's built-in HTML entity decoder
    const textarea = document.createElement('textarea')
    textarea.innerHTML = text
    return textarea.value
  } else {
    // Fallback for non-browser environments
    return text
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&#x27;/g, "'")
      .replace(/&#x2F;/g, '/')
  }
}

/**
 * Sanitizes text that already contains highlighting markup
 * This ensures that only safe highlighting markup is preserved
 * 
 * @param text The text containing highlighting markup
 * @returns Sanitized text with safe highlighting
 */
export const sanitizeHighlightedText = (text: string): string => {
  if (!text) return ''
  
  // First decode HTML entities
  const decodedText = decodeHtmlEntities(text)
  
  // Remove any potentially dangerous HTML tags while preserving highlighting
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = decodedText
  
  // Remove dangerous elements
  const dangerousElements = tempDiv.querySelectorAll('script, iframe, object, embed, form, input, style')
  dangerousElements.forEach(el => el.remove())
  
  // Remove dangerous attributes from all elements
  const allElements = tempDiv.querySelectorAll('*')
  allElements.forEach(el => {
    // Remove event attributes
    Array.from(el.attributes).forEach(attr => {
      if (attr.name.startsWith('on')) {
        el.removeAttribute(attr.name)
      }
    })
    
    // Remove javascript: links
    if (el.getAttribute('href')?.startsWith('javascript:')) {
      el.removeAttribute('href')
    }
  })
  
  // Normalize highlighting markup - convert old 'highlight' class to new 'search-highlight' class
  let sanitizedHtml = tempDiv.innerHTML
  
  // Convert old highlight class to new search-highlight class
  sanitizedHtml = sanitizedHtml.replace(/<em class="highlight">/gi, '<em class="search-highlight">')
  sanitizedHtml = sanitizedHtml.replace(/<em class=&quot;highlight&quot;>/gi, '<em class="search-highlight">')
  
  return sanitizedHtml
}

/**
 * Vue composable for safe highlighting functionality
 * Provides reactive highlighting that can be used in Vue components
 */
export const useSafeHighlighting = () => {
  /**
   * Creates a safe highlight object for Vue template rendering
   * @param text The text to highlight
   * @param searchTerm The search term to highlight
   * @returns Reactive highlight object
   */
  const createHighlight = (text: string, searchTerm: string) => {
    return createSafeHighlight(text, searchTerm)
  }
  
  /**
   * Creates safe HTML highlighting for use with v-html (when necessary)
   * @param text The text to highlight
   * @param searchTerm The search term to highlight
   * @returns Safe HTML string
   */
  const createHighlightHtml = (text: string, searchTerm: string) => {
    return createSafeHighlightHtml(text, searchTerm)
  }
  
  /**
   * Processes API text that may already contain highlighting
   * @param text The text from API
   * @param searchTerm The search term
   * @returns Safe HTML string
   */
  const processApiText = (text: string, searchTerm: string) => {
    return processApiHighlightedText(text, searchTerm)
  }
  
  return {
    createHighlight,
    createHighlightHtml,
    processApiText,
    escapeHtml,
    decodeHtmlEntities
  }
}
